import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router';
import { type QueryClient } from '@tanstack/react-query';

import { useNavigateBack } from '#apps/client-web/app/hooks/use-navigate-back';
import { type AuditFormService } from '#apps/client-web/features/audit-form/services/audit-form';
import {
  type FileContent,
  type FileStorageService,
} from '#apps/client-web/features/file-storage/services/file-storage';
import { useSaveFileMutation } from '#apps/client-web/features/file-storage/hooks/use-save-file-mutation';
import { useDeleteFileMutation } from '#apps/client-web/features/file-storage/hooks/use-delete-file-mutation';
import { useLoadFilesQuery } from '#apps/client-web/features/file-storage/hooks/use-load-file-query';
import { useLoadAuditFormByType } from '#apps/client-web/features/audit-form/hooks/use-load-audit-form-by-type-query';
import { useSaveAuditFormMutation } from '#apps/client-web/features/audit-form/hooks/use-save-audit-form-mutation';
import { AUDIT_FORMS_QUERY_KEY } from '#apps/client-web/features/audit-form/types/react-query';

import { type LoggingContext } from '#packages/data-collection/types/logging-context';
import { type AuditId } from '#packages/data-collection/models/audit';
import {
  type QueryPageState,
  type MutationPageState,
} from '#packages/data-collection/types/page-state';
import {
  AuditBackgroundFormSchema,
  type AuditBackgroundForm,
} from '#packages/data-collection/models/audit-background-form';
import { isNil } from '#packages/data-collection/libs/lodash';

import { type EditAuditFormBackgroundData } from './types';
import { fromFormData, toFormData } from './mappers';
import { emptyFormData } from './constants';

export type AuditFormBackgroundPageViewModel = ReturnType<
  typeof useAuditFormBackgroundPageViewModel
>;

type AuditFormBackgroundPageViewModelProps = LoggingContext & {
  auditFormService: AuditFormService;
  fileStorageService: FileStorageService;
  queryClient: QueryClient;
};

export const useAuditFormBackgroundPageViewModel = ({
  logger,
  auditFormService,
  fileStorageService,
  queryClient,
}: AuditFormBackgroundPageViewModelProps) => {
  const goBack = useNavigateBack({ logger });

  const { auditId } = useParams<{
    auditId: AuditId;
  }>();

  const auditFormQuery = useLoadAuditFormByType<AuditBackgroundForm>({
    auditFormService,
    auditId: auditId!,
    formType: 'background',
    queryClient,
  });

  const saveFileStorageMutation = useSaveFileMutation({
    fileStorageService,
    queryClient,
  });

  const deleteFileStorageMutation = useDeleteFileMutation({
    fileStorageService,
    queryClient,
  });

  // Collect all file IDs in order: meters, controls, envelope (coverPhoto, window/door/wall/roofing entries).
  // This order must match the flat `files` array produced by fileStorageQuery
  // and consumed by toFormData.
  const fileIds = useMemo(
    () => [
      ...(auditFormQuery.data?.general.meters ?? []).flatMap((meter) =>
        (meter.attachments ?? []).map((attachment) => attachment.name),
      ),
      ...(auditFormQuery.data?.controls.attachments ?? []).map(
        (attachment) => attachment.name,
      ),
      ...(auditFormQuery.data?.envelope.coverPhoto ?? []).map(
        (attachment) => attachment.name,
      ),
      ...(auditFormQuery.data?.envelope.windowTypes ?? []).flatMap((entry) =>
        (entry.attachments ?? []).map((attachment) => attachment.name),
      ),
      ...(auditFormQuery.data?.envelope.doorTypes ?? []).flatMap((entry) =>
        (entry.attachments ?? []).map((attachment) => attachment.name),
      ),
      ...(auditFormQuery.data?.envelope.wallTypes ?? []).flatMap((entry) =>
        (entry.attachments ?? []).map((attachment) => attachment.name),
      ),
      ...(auditFormQuery.data?.envelope.roofingTypes ?? []).flatMap((entry) =>
        (entry.attachments ?? []).map((attachment) => attachment.name),
      ),
      ...(auditFormQuery.data?.buildingAndSystems.attachments ?? []).map(
        (attachment) => attachment.name,
      ),
    ],
    [
      auditFormQuery.data?.general.meters,
      auditFormQuery.data?.controls.attachments,
      auditFormQuery.data?.envelope.coverPhoto,
      auditFormQuery.data?.envelope.windowTypes,
      auditFormQuery.data?.envelope.doorTypes,
      auditFormQuery.data?.envelope.wallTypes,
      auditFormQuery.data?.envelope.roofingTypes,
      auditFormQuery.data?.buildingAndSystems.attachments,
    ],
  );

  const fileStorageQuery = useLoadFilesQuery({
    fileStorageService,
    queryClient,
    fileIds,
  });

  const saveAuditFormMutation = useSaveAuditFormMutation<AuditBackgroundForm>({
    auditFormService,
    queryClient,
    auditId: auditId!,
    formType: 'background',
  });

  const form = useForm<EditAuditFormBackgroundData>({
    reValidateMode: 'onBlur',
    mode: 'onBlur',

    defaultValues: {
      ...emptyFormData,
    },
  });

  const onSave = useCallback(
    (formData: EditAuditFormBackgroundData) => {
      if (loadAuditFormState.state !== 'success') {
        logger?.debug(
          'Cannot update background form, page is not in success state',
        );
        return;
      }

      logger?.debug({ auditId, formData }, 'Updating background form data');

      // Compute all file IDs currently referenced in the form
      const newFileIds = [
        ...(formData.general.meters ?? []).flatMap((meter) =>
          (meter.attachments ?? []).map((attachment) => attachment.name),
        ),
        ...(formData.controls.attachments ?? []).map(
          (attachment) => attachment.name,
        ),
        ...(formData.envelope.coverPhoto ?? []).map(
          (attachment) => attachment.name,
        ),
        ...(formData.envelope.windowTypes ?? []).flatMap((entry) =>
          (entry.attachments ?? []).map((attachment) => attachment.name),
        ),
        ...(formData.envelope.doorTypes ?? []).flatMap((entry) =>
          (entry.attachments ?? []).map((attachment) => attachment.name),
        ),
        ...(formData.envelope.wallTypes ?? []).flatMap((entry) =>
          (entry.attachments ?? []).map((attachment) => attachment.name),
        ),
        ...(formData.envelope.roofingTypes ?? []).flatMap((entry) =>
          (entry.attachments ?? []).map((attachment) => attachment.name),
        ),
        ...(formData.buildingAndSystems.attachments ?? []).map(
          (attachment) => attachment.name,
        ),
      ];

      // Delete files that have been removed
      const deletedFileIds = fileIds.filter(
        (fileId) => !newFileIds.includes(fileId),
      );
      deletedFileIds.forEach((fileId) => {
        deleteFileStorageMutation.mutate({ fileId });
      });

      // Persist files for all meter attachments
      (formData.general.meters ?? []).forEach((meter) => {
        (meter.attachments ?? []).forEach((attachment) => {
          saveFileStorageMutation.mutate({
            fileId: attachment.name,
            content: attachment.file,
          });
        });
      });

      // Persist files for controls attachments
      (formData.controls.attachments ?? []).forEach((attachment) => {
        saveFileStorageMutation.mutate({
          fileId: attachment.name,
          content: attachment.file,
        });
      });

      // Persist files for envelope attachments
      [
        ...(formData.envelope.coverPhoto ?? []),
        ...(formData.envelope.windowTypes ?? []).flatMap(
          (entry) => entry.attachments ?? [],
        ),
        ...(formData.envelope.doorTypes ?? []).flatMap(
          (entry) => entry.attachments ?? [],
        ),
        ...(formData.envelope.wallTypes ?? []).flatMap(
          (entry) => entry.attachments ?? [],
        ),
        ...(formData.envelope.roofingTypes ?? []).flatMap(
          (entry) => entry.attachments ?? [],
        ),
        ...(formData.buildingAndSystems.attachments ?? []),
      ].forEach((attachment) => {
        saveFileStorageMutation.mutate({
          fileId: attachment.name,
          content: attachment.file,
        });
      });

      // Encode existing data to raw format, or start with empty object
      const previousDataEncoded = AuditBackgroundFormSchema.encode(
        auditFormQuery.data ??
          AuditBackgroundFormSchema.decode({
            type: 'background',
            ...emptyFormData,
          }),
      );

      const mappedFormDataEncoded = AuditBackgroundFormSchema.encode({
        type: 'background',
        ...fromFormData(formData),
      });

      logger?.debug(
        { formData, mappedFormDataEncoded },
        'formData before and after mapping',
      );

      const auditBackgroundForm = AuditBackgroundFormSchema.decode({
        ...previousDataEncoded,
        ...mappedFormDataEncoded,
      });

      saveAuditFormMutation.mutate(
        { auditId: auditId!, form: auditBackgroundForm },
        {
          onSuccess: (savedForm: AuditBackgroundForm) => {
            logger?.debug(
              { auditId, savedForm },
              'background form updated successfully',
            );
            // invalidate audit background form queries
            queryClient
              .invalidateQueries({
                queryKey: [
                  AUDIT_FORMS_QUERY_KEY,
                  auditId,
                  auditBackgroundForm.type,
                ],
              })
              .then(() => {
                logger?.debug('background form queries invalidated');
                goBack();
              });
          },
          onError: (err) => {
            logger?.error({ err }, 'Failed to update background form');
            throw err;
          },
        },
      );
    },
    [
      saveAuditFormMutation.mutate,
      auditId,
      saveFileStorageMutation.mutate,
      deleteFileStorageMutation.mutate,
      fileIds,
      auditFormQuery.data,
      AuditBackgroundFormSchema.encode,
      AuditBackgroundFormSchema.decode,
    ],
  );

  const updateMutationState = useMemo<MutationPageState>(() => {
    if (
      (saveAuditFormMutation.isIdle || saveAuditFormMutation.isSuccess) &&
      (saveFileStorageMutation.isIdle || saveFileStorageMutation.isSuccess) &&
      (deleteFileStorageMutation.isIdle || deleteFileStorageMutation.isSuccess)
    ) {
      return { state: 'idle' };
    }

    if (
      saveAuditFormMutation.isPending ||
      saveFileStorageMutation.isPending ||
      deleteFileStorageMutation.isPending
    ) {
      return { state: 'pending' };
    }

    if (saveAuditFormMutation.isError) {
      return { state: 'error', error: saveAuditFormMutation.error };
    }

    if (saveFileStorageMutation.isError) {
      return { state: 'error', error: saveFileStorageMutation.error };
    }

    if (deleteFileStorageMutation.isError) {
      return { state: 'error', error: deleteFileStorageMutation.error };
    }

    throw new Error('Unhandled mutation state');
  }, [
    saveAuditFormMutation.isIdle,
    saveAuditFormMutation.isPending,
    saveAuditFormMutation.isSuccess,
    saveAuditFormMutation.isError,
    saveAuditFormMutation.error,
    saveFileStorageMutation.isIdle,
    saveFileStorageMutation.isPending,
    saveFileStorageMutation.isSuccess,
    saveFileStorageMutation.isError,
    saveFileStorageMutation.error,
    deleteFileStorageMutation.isIdle,
    deleteFileStorageMutation.isPending,
    deleteFileStorageMutation.isSuccess,
    deleteFileStorageMutation.isError,
    deleteFileStorageMutation.error,
  ]);

  const loadAuditFormState = useMemo<
    QueryPageState<{
      form: AuditBackgroundForm | undefined;
      files: (FileContent | undefined)[];
    }>
  >(() => {
    const areFilesLoaded =
      fileIds.length === 0 ||
      fileStorageQuery.every((query) => query.isSuccess);
    if (auditFormQuery.isSuccess && areFilesLoaded) {
      const files =
        fileStorageQuery.map((item) => item.data ?? undefined) ?? undefined;
      return {
        state: 'success',
        data: { form: auditFormQuery.data ?? undefined, files },
      };
    }

    const areFilesLoading =
      fileIds.length > 0 &&
      fileStorageQuery.some((query) => query.isLoading || query.isPending);
    if (
      auditFormQuery.isLoading ||
      auditFormQuery.isPending ||
      areFilesLoading
    ) {
      return { state: 'loading' };
    }

    if (auditFormQuery.isError) {
      return {
        state: 'error',
        error: auditFormQuery.error,
      };
    }

    const fileError = fileStorageQuery.find((query) => query.isError)?.error;
    if (!isNil(fileError)) {
      return {
        state: 'error',
        error: fileError,
      };
    }

    throw new Error('Unhandled query state');
  }, [
    auditFormQuery.data,
    auditFormQuery.isSuccess,
    auditFormQuery.isLoading,
    auditFormQuery.isPending,
    auditFormQuery.isError,
    auditFormQuery.error,
    fileStorageQuery,
    fileIds,
  ]);

  // sync form default values when audit details are loaded
  useEffect(() => {
    if (loadAuditFormState.state !== 'success') {
      return;
    }

    logger?.debug(
      { auditbackgroundForm: auditFormQuery.data },
      'Audit background form default values',
    );

    // update form default values
    form.reset(
      toFormData(loadAuditFormState.data.form, loadAuditFormState.data.files),
    );
  }, [auditFormQuery.data, auditFormQuery.isSuccess]);

  return {
    auditFormQuery,
    loadAuditFormState,

    form,
    onSubmit: form.handleSubmit(onSave),
    updateMutationState,

    auditId,
  };
};
